/**
 * lazyComRetry — a aba antiga não pode virar tela de erro por causa de um deploy.
 *
 * O portal é dividido em pedaços (chunks) que o navegador só baixa quando a
 * pessoa entra na tela. O nome do arquivo tem o hash do build. Quando sai uma
 * versão nova, a aba que já estava aberta continua pedindo os pedaços do build
 * ANTIGO — e antes de 20/09/2026 esses arquivos sumiam do servidor na troca de
 * slot: navegar quebrava a tela ("Loading chunk failed").
 *
 * Duas travas, nesta ordem:
 *   1. o deploy agora mantém os arquivos da versão anterior no ar (deploy-frontend.sh);
 *   2. se ainda assim faltar um pedaço, a página recarrega UMA vez sozinha, na
 *      mesma URL — a pessoa cai exatamente onde ia entrar, com o código novo.
 *
 * A marca fica em sessionStorage (morre com a aba) e guarda a URL: duas telas
 * diferentes podem tentar uma vez cada, e nunca entra em laço de recarga.
 */
import { lazy, ComponentType } from 'react';

const MARCA = 'portal:recarga-pedaco-faltando';

// sessionStorage pode explodir (janela anônima, cookies bloqueados): nunca derrubar a tela por isso
const ler = (): string | null => {
  try { return window.sessionStorage.getItem(MARCA); } catch { return null; }
};
const gravar = (v: string) => {
  try { window.sessionStorage.setItem(MARCA, v); } catch { /* sem memória de sessão: segue */ }
};
const limpar = () => {
  try { window.sessionStorage.removeItem(MARCA); } catch { /* idem */ }
};

/** Erro de pedaço que não existe mais no servidor (varia por navegador). */
export function ePedacoFaltando(erro: unknown): boolean {
  const e = erro as { name?: string; message?: string; code?: string } | null;
  const texto = `${e?.name || ''} ${e?.code || ''} ${e?.message || ''}`;
  return /ChunkLoadError|Loading chunk|Loading CSS chunk|dynamically imported module|Importing a module script failed|error loading dynamically imported/i.test(texto);
}

export function lazyComRetry<T extends ComponentType<any>>(
  importar: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    try {
      const modulo = await importar();
      limpar(); // chegou inteiro: a aba está em dia
      return modulo;
    } catch (erro) {
      const aqui = window.location.href;
      if (!ePedacoFaltando(erro) || ler() === aqui) throw erro; // já tentamos nesta tela

      gravar(aqui);
      window.location.reload();
      // a página está indo embora: devolve uma promessa que nunca resolve
      // (em vez de propagar o erro e piscar a tela vermelha antes da recarga)
      return new Promise<{ default: T }>(() => {});
    }
  });
}

export default lazyComRetry;
